import * as d3 from 'd3';
import { calculateMaxWidth, generateRangeArray } from 'utils/circuit';
import {
  classicRegisterLineSidtance,
  dy,
  gateHeight,
  gatesDistance,
  gateWidth,
  horizontalMargin,
} from './constants';

const registerStroke = 'rgba(255, 255, 255, 0.15)';

const createCircuit = ({
  numQubits,
  layers,
}) => {
  const layersLength = layers.length;
  const minLayerCount = 4;
  const minWidth = (minLayerCount + 1) * gatesDistance + 2 * horizontalMargin;
  const height = (numQubits + 2) * dy;

  const nonBarrierLayers = layers.filter(
    (item) => !(item.length === 1 && item[0].name === 'barrier'),
  );

  const totalWidth = nonBarrierLayers.reduce(
    (sum, layer) => sum + calculateMaxWidth(layer) + gatesDistance,
    horizontalMargin,
  );
  const width = Math.max(totalWidth, minWidth);

  const arr2d = nonBarrierLayers.map((layer, layerIndex) => {
    const baseGateX = nonBarrierLayers
      .slice(0, layerIndex)
      .reduce(
        (sum, prevLayer) => sum + calculateMaxWidth(prevLayer) + gatesDistance,
        horizontalMargin,
      );

    return layer.map(({
      name,
      parametersList,
      numControls,
      qubits,
    }) => {
      const controls = qubits.slice(0, numControls);
      const targets = qubits.slice(numControls);
      const sortedQubits = [...targets].sort((a, b) => a - b);
      const containedLayers = generateRangeArray(
        sortedQubits[0],
        sortedQubits[sortedQubits.length - 1],
      );
      const firstTargetNumber = sortedQubits[0];
      const dynamicWidth = calculateMaxWidth(layer);

      return {
        name,
        parametersList,
        qubits: {
          registers: targets.map((registerNumber) => ({
            x: baseGateX + dynamicWidth / 2,
            y: (registerNumber + 1) * dy,
            registerNumber,
          })),
          rect: {
            x: baseGateX,
            y: (firstTargetNumber + 1) * dy - gateHeight / 2,
            width: dynamicWidth,
            height: (containedLayers.length - 1) * dy + gateHeight,
            get center() {
              return {
                x: this.x + this.width / 2,
                y: this.y + this.height / 2,
              };
            },
          },
          get measure() {
            return {
              mx: horizontalMargin + gatesDistance * (layerIndex + 1) - gateWidth / 2,
              my: dy * (numQubits + 1),
            };
          },
        },
        controls: controls.map((controlRegister) => ({
          x: baseGateX + dynamicWidth / 2,
          y: (controlRegister + 1) * dy,
          registerNumber: controlRegister,
        })),
      };
    });
  });

  const registers = Array.from({ length: numQubits }, (_, i) => ({
    x1: horizontalMargin,
    y1: dy * (i + 1),
    x2: width - horizontalMargin / 2,
    y2: dy * (i + 1),
    stroke: registerStroke,
    strokeWidth: 2,
  }));

  const classicRegisters = [
    {
      x1: horizontalMargin,
      y1: dy * (numQubits + 1),
      x2: width - horizontalMargin / 2,
      y2: dy * (numQubits + 1),
      stroke: registerStroke,
      strokeWidth: 2,
    },
    {
      x1: horizontalMargin,
      y1: dy * (numQubits + 1) + classicRegisterLineSidtance,
      x2: width - horizontalMargin / 2,
      y2: dy * (numQubits + 1) + classicRegisterLineSidtance,
      stroke: registerStroke,
      strokeWidth: 2,
    },
  ];

  return {
    gates: d3.merge(arr2d),
    registers,
    layersLength,
    width,
    height,
    classicRegisters,
  };
};

export default createCircuit;
